import React from 'react';
import PropTypes from 'prop-types';
import { cva } from 'class-variance-authority';
import { cn } from '~/lib/utils';

const buttonVariants = cva(
	'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold transition-all duration-200 outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				default: 'bg-primary text-primary-foreground shadow-sm hover:brightness-110',
				secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/85',
				outline: 'border border-border bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground',
				ghost: 'bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground',
				destructive: 'bg-destructive text-destructive-foreground hover:brightness-110'
			},
			size: {
				default: 'h-11 px-4 py-2',
				sm: 'h-9 rounded-md px-3',
				xs: 'h-7 rounded px-2 text-xs',
				lg: 'h-12 rounded-lg px-6',
				icon: 'h-10 w-10'
			}
		},
		defaultVariants: {
			variant: 'default',
			size: 'default'
		}
	}
);

const Button = React.forwardRef(({
	className, variant, size, asChild = false, disabled, type, ...props
}, ref) => {
	const Comp = asChild ? 'span' : 'button';

	return (
		<Comp
			className={cn(buttonVariants({ variant, size }), disabled && 'disabled', className)}
			ref={ref}
			disabled={disabled}
			// Default native buttons to type="button" so a Button rendered inside a
			// <form> doesn't implicitly submit it; callers can still pass type="submit".
			type={Comp === 'button' ? (type || 'button') : type}
			{...props}
		/>
	);
});

Button.propTypes = {
	className: PropTypes.string,
	variant: PropTypes.oneOf(['default', 'secondary', 'outline', 'ghost', 'destructive']),
	size: PropTypes.oneOf(['default', 'sm', 'xs', 'lg', 'icon']),
	asChild: PropTypes.bool,
	disabled: PropTypes.bool,
	type: PropTypes.string
};

Button.displayName = 'Button';

export { Button, buttonVariants };
